import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../../Components/Ui/Card';
import Button from '../../../Components/Ui/Button';
import { ArrowLeft, FileText, Building2, Mail, Phone, MapPin } from 'lucide-react';
import { useGetUserInfoQuery } from '../../../app/services/authApi';

import DocumentViewer from '../../../Components/Ui/DocumentViewer';
import Loader from '../../../Components/Ui/Loader';

const DistributorSellers = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: { data: seller } = {}, isLoading } = useGetUserInfoQuery(id);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  if (!seller) {
    return <div className="text-center text-red-500 text-xl mt-10">Seller not found.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Seller Overview</h2>
        <Button onClick={() => navigate('/brand/distributor')} variant="outline">
          <ArrowLeft size={16} className="mr-2" />
          Back to Sellers
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="p-6 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            {/* Column 1: Seller & GST Info */}
            <div className="space-y-6">
              {/* Seller Information */}
              <div className="flex items-start">
                <Building2 className="h-6 w-6 text-primary mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Seller Information</h3>
                  <div className="mt-2 space-y-2 text-sm">
                    <p>
                      <span className="font-medium text-gray-500">Trade Name:</span>{' '}
                      <span className="text-gray-900">{seller.tradeName || 'N/A'}</span>
                    </p>
                    <p>
                      <span className="font-medium text-gray-500">Owner Name:</span>{' '}
                      <span className="text-gray-900">{seller.name || 'N/A'}</span>
                    </p>
                    <p>
                      <span className="font-medium text-gray-500">Store Name:</span>{' '}
                      <span className="text-gray-900">{seller.storeName || 'N/A'}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* GST Information */}
              <div className="flex items-start">
                <FileText className="h-6 w-6 text-primary mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">GST Information</h3>
                  <div className="mt-2 space-y-2 text-sm">
                    <p>
                      <span className="font-medium text-gray-500">GST Number:</span>{' '}
                      <span className="font-mono text-gray-900">{seller.gstNumber || 'N/A'}</span>
                    </p>
                    {seller.gstCertificate && (
                      <div className="flex items-center space-x-3 pt-1">
                        <span className="font-medium text-gray-500">Certificate:</span>
                        <DocumentViewer
                          fileUrl={seller.gstCertificate}
                          buttonText="View Certificate"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Column 2: Contact & Address */}
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="flex items-start">
                <Mail className="h-6 w-6 text-primary mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Contact Information</h3>
                  <div className="mt-2 space-y-2 text-sm">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      <a href={`mailto:${seller.email}`} className="text-primary hover:underline">
                        {seller.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      <span className="text-gray-900">{seller.phone || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Details */}
              <div className="flex items-start">
                <MapPin className="h-6 w-6 text-primary mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Primary Address</h3>
                  {seller.primaryAddress ? (
                    <address className="mt-2 text-sm text-gray-800 not-italic">
                      {seller.primaryAddress.line1}
                      <br />
                      {seller.primaryAddress.line2 && (
                        <>
                          {seller.primaryAddress.line2}
                          <br />
                        </>
                      )}
                      {seller.primaryAddress.city}, {seller.primaryAddress.state}{' '}
                      {seller.primaryAddress.zipCode}
                    </address>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500">No address available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DistributorSellers;
